import get from "lodash/get";
import React, {Component} from "react";
import compose from "lodash/flowRight";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import {withTranslation, Constants} from "@unibuddy/intl";
import {Query} from "@apollo/client/react/components";
import {Spinner, VisuallyHidden, Box} from "@unibuddy/patron";

import {productSpec, sourceTracking} from "shared/AnalyticsNew/constants/productSpecification";
import RecordAnalytics from "shared/Analytics/RecordAnalytics";
import withAnalytics from "shared/AnalyticsNew/withAnalytics/withAnalytics";
import withOpenInJigglypuff from "shared/Navigation/hocs/withOpenInJigglypuff";
import {withRouteParamTheme} from "shared/Styling/hocs/withTheme";
import withSourceTracking from "shared/SourceTracking/withSourceTracking/withSourceTracking";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";
import {PageTitle} from "../../../components/PageTitle/PageTitle";
import mentorListQuery from "../Mentor/queries/mentorListQuery";
import buddiesQuery from "../Buddies/queries/buddiesQuery";
import MentorPreviewCard from "../../../widgets/modules/Mentor/components/MentorPreviewCard/MentorPreviewCard";
import {StaffBuddycard} from "../BuddiesNew/containers/StaffBuddycard";
import {IntlNamespaces} from "../../../translations/types";
import {INTL_BUDDIES} from "../../../translations/common";
import {sequenceFilterMentorListQuery} from "../Mentor/queries/sequenceFilterMentorListQuery";

export class InternalFilteredMentorListComponent extends Component {
    componentDidMount() {
        const filter = get(this.props, "match.params.filter", "");
        const isStaff = ["role", "department", "staff"].includes(filter);
        RecordAnalytics.page(this.props, "University Buddy Card List"); // Legacy Event
        this.props.sourceTracking.updateSourceTrackingParamLocally(
            sourceTracking.sourceTrackingParams.properties.UB_SOURCE,
            `${productSpec.embeddedUniversityBuddyCards.NAME}${isStaff ? " - Staff" : ""}`,
        );
        this.props.analytics.setAnalyticsValue(
            sourceTracking.sourceTrackingParams.properties.UB_SOURCE,
            `${productSpec.embeddedUniversityBuddyCards.NAME}${isStaff ? " - Staff" : ""}`,
        );
        this.props.analytics.events.pageViewed({
            prefix: this.props.analytics.pageNames.buddiesList,
        });
        const {isProductOpenedEventEnabled} = this.props;
        if (isProductOpenedEventEnabled) {
            this.props.analytics.events.productOpened({
                name: productSpec.embeddedUniversityBuddyCards.NAME,
                product: `${productSpec.embeddedUniversityBuddyCards.NAME}${
                    isStaff ? " - Staff" : ""
                }`,
                universitySlug: this.props.match.params.universitySlug,
                ubLang: this.props?.i18n?.language ?? Constants.DEFAULT_LOCALE,
            });
        }
    }

    componentDidUpdate(prevProps) {
        const filter = get(this.props, "match.params.filter", "");
        const isStaff = ["role", "department", "staff"].includes(filter);
        if (prevProps.isProductOpenedEventEnabled !== this.props.isProductOpenedEventEnabled) {
            this.props.analytics.events.productOpened({
                name: productSpec.embeddedUniversityBuddyCards.NAME,
                product: `${productSpec.embeddedUniversityBuddyCards.NAME}${
                    isStaff ? " - Staff" : ""
                }`,
                universitySlug: this.props.match.params.universitySlug,
                ubLang: this.props?.i18n?.language ?? Constants.DEFAULT_LOCALE,
            });
        }
    }

    getRedirectUrl(path) {
        const widgetUrl =
            this.props.university?.widgetUrl ?? this.props.university?.widgetUrls[0]?.url ?? "";
        try {
            const url = new URL(widgetUrl);
            url.searchParams.set("unibuddy", path);
            return url.toString();
        } catch (error) {
            console.log(error);
        }
        return widgetUrl;
    }

    onChatNow = (buddyId, buddyPosition) => {
        if (buddyId) {
            const url = this.getRedirectUrl(`inbox/chatwith/${buddyId}`);
            RecordAnalytics.event(this.props, "Buddy Card Chat Now Clicked", {
                buddyId,
                buddyPosition,
            });
            window.open(this.props.sourceTracking.redirectWithSourceTracking(url), "_blank");
        }
    };

    onAvatarClicked(buddyId, isStaff = false) {
        if (buddyId) {
            RecordAnalytics.event(this.props, "Buddy Card Avatar Clicked", {buddyId});
            const url = this.getProfileURL(buddyId, isStaff);
            window.open(url, "_blank");
        }
    }

    onSeeMore = (buddyId, isStaff = false) => {
        if (buddyId) {
            RecordAnalytics.event(this.props, "Buddy Card See More Clicked", {buddyId});
            const url = this.getProfileURL(buddyId, isStaff);
            window.open(url, "_blank");
        }
    };

    getProfileURL = (buddyId, isStaff = false) => {
        if (buddyId) {
            const path = isStaff ? `buddies/staff/${buddyId}` : `buddies/${buddyId}`;
            RecordAnalytics.event(this.props, "Buddy Card See More Clicked", {buddyId});
            const url = this.getRedirectUrl(path);
            return this.props.sourceTracking.redirectWithSourceTracking(url);
        }
    };

    renderMentors(mentors) {
        return mentors.map((mentor, i) => (
            <Box maxW="400px" marginBottom="medium" marginX="auto" key={mentor.id}>
                <MentorPreviewCard
                    mentor={mentor}
                    onChatNow={this.onChatNow.bind(this, mentor.id, i + 1)}
                    onSeeMoreClicked={this.onSeeMore.bind(this, mentor.id, false)}
                    onAvatarClicked={this.onAvatarClicked.bind(this, mentor.id, false)}
                    rootUrl={this.getProfileURL(mentor.id)}
                    buddyPosition={i + 1}
                    university={this.props.university}
                    isInternalBuddycard
                />
            </Box>
        ));
    }

    renderStaff(staff) {
        const staffCards = staff.map((staffMem, i) => {
            staffMem = {
                ...staffMem,
                staffBuddyName: `${staffMem.firstName} (staff)`,
            };
            return (
                <Box maxW="400px" marginBottom="medium" marginX="auto" key={staffMem.id}>
                    <StaffBuddycard
                        buddy={staffMem}
                        rootUrl={this.getProfileURL(staffMem.id, true)}
                        onChatNow={this.onChatNow.bind(this, staffMem.id, i + 1)}
                        onSeeMore={this.onSeeMore.bind(this, staffMem.id, true)}
                        onAvatarClicked={this.onAvatarClicked.bind(this, staffMem.id, true)}
                        isInternalBuddycard
                    />
                </Box>
            );
        });
        return staffCards;
    }

    render() {
        const filter = get(this.props, "match.params.filter", "");
        const filterVal = get(this.props, "match.params.value", "");
        const isStaff = ["role", "department", "staff"].includes(filter);
        const universitySlug = get(this.props, "match.params.universitySlug", "");
        const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", this.props.university);
        let query;
        let variables;
        let domesticOrInternational = null;
        const sequenceFilter = [];

        if (filter !== "mentorId" && !isStaff) {
            let applyDegreeFilter = false;
            if (filter === "domesticOrInternational") {
                domesticOrInternational = filterVal;
            } else {
                const filterKeyMap = {
                    level: "degree_level",
                    degreeId: "degree",
                    countryId: "country",
                };
                const filterKey = filterKeyMap[filter];

                // sequenceFilter should be of type [{field: string, value: string[]}]
                // or can be an empty array if domesticOrInternational has a value
                if (filterKey) sequenceFilter.push({field: filterKey, value: filterVal});
                if (filterKey && filterKey === "degree") applyDegreeFilter = true;
            }

            query = sequenceFilterMentorListQuery;
            variables = {
                universitySlug,
                sequenceFilter,
                applyDegreeFilter,
                domesticOrInternational,
                isDegreeTypesEnabled,
                limit: 4,
            };
        } else if (filter === "mentorId" && !isStaff) {
            query = mentorListQuery;
            variables = {
                universitySlug,
                [filter]: filterVal,
                isDegreeTypesEnabled,
            };
        } else if (isStaff) {
            query = buddiesQuery;
            variables = {
                universitySlug,
                idIn: filter === "staff" ? [filterVal] : null,
                role: "staff",
                staffFields: filter !== "staff" ? JSON.stringify({[filter]: filterVal}) : "{}",
            };
        }
        const isStaffProfilesEnabled = isFeatureEnabled("staffProfiles", this.props.university);
        return (
            <div id="mentors-container">
                <PageTitle title={this.props.t(INTL_BUDDIES.key, INTL_BUDDIES.defaultValue)} />
                <Query query={query} variables={variables} skip={!universitySlug}>
                    {({data, loading, error}) => {
                        if (error) console.error({error});
                        if (loading || !data) {
                            return (
                                <div data-test-id="query-loading">
                                    <VisuallyHidden>Loading</VisuallyHidden>
                                    <Spinner color="#E4E8EB" aria-hidden />
                                </div>
                            );
                        }
                        let mentors;
                        if (query === mentorListQuery) {
                            mentors = data?.mentorList?.mentors ?? [];
                        } else {
                            mentors = data?.sequenceFilterMentorList?.mentors ?? [];
                        }
                        const staff = data?.buddies?.users ?? [];
                        return isStaff
                            ? isStaffProfilesEnabled && this.renderStaff(staff)
                            : this.renderMentors(mentors);
                    }}
                </Query>
            </div>
        );
    }
}

export default compose(
    withRouter,
    connect(),
    withAnalytics,
    withSourceTracking,
    withRouteParamTheme(),
    withOpenInJigglypuff,
    withTranslation(IntlNamespaces.COMMON),
)(InternalFilteredMentorListComponent);
